import React, { useState } from 'react';
import Header from '../components/Header';
import CurlGenerator from '../components/CurlGenerator';
import { 
  CodeBracketIcon,
  ServerIcon,
  KeyIcon,
  DocumentTextIcon,
  ClipboardDocumentIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

const AgentRegistrationAPI = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [copiedCode, setCopiedCode] = useState(null);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

const agentRegistrationTemplate = {
  endpoint: 'https://api.taar.ai/ext/agent/register',
  method: 'POST',
  headers: [
    { label: 'Authorization', value: '{token}' }
  ],
  fields: [
    { name: 'agentId', label: 'Agent ID', required: true, default: 'xyz@xyz.com', 
      description: 'Unique identifier for the agent (email format)' },
    { name: 'nonce', label: 'Nonce', required: true, default: 'random-nonce-value',
      description: 'One-time random value for request validation' },
    { name: 'token', label: 'Auth Token', required: true, default: 'YOUR_TOKEN',
      description: 'Your company\'s long-lived auth token' }
  ],
  bodyTemplate: (form) => {
    return JSON.stringify({
      agentId: form.agentId,
      nonce: form.nonce
    }, null, 2);
  }
};

  const apiExamples = {
    curl: `curl -X POST https://api.taar.ai/ext/agent/register \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "agentId": "agent@example.com",
    "nonce": "random-nonce-value"
  }'`,
    js: `// Using fetch API
const registerAgent = async (agentData) => {
  const response = await fetch('https://api.taar.ai/ext/agent/register', {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer YOUR_TOKEN',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      agentId: agentData.email,
      nonce: generateNonce()
    })
  });
  
  const result = await response.json();
  return result;
};`,
    response: `{
  "success": true,
  "message": "Agent registered successfully",
  "data": {
    "agentId": "agent@example.com",
    "credentials": {
      "webSocketUrl": "wss://api.taar.ai/agent/ws",
      "agentToken": "agent_token_here"
    },
    "registeredAt": "2024-01-15T10:30:00Z"
  }
}`,
    error: `{
  "success": false,
  "error": "Agent registration failed",
  "details": "Invalid agent ID format",
  "code": 400
}`
  };

  const headerActions = [
    {
      href: "https://app.taar.ai",
      target: "_blank",
      rel: "noopener noreferrer",
      className: "bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors",
      children: "Open Dashboard"
    },
    {
      href: "/docs/agent-registration",
      className: "text-gray-500 hover:text-gray-700",
      children: "Setup Guide →"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header 
        subtitle="Agent Registration API"
        actions={headerActions}
      />

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview', icon: DocumentTextIcon },
              { id: 'api', label: 'API Reference', icon: CodeBracketIcon },
              { id: 'auth', label: 'Authentication', icon: KeyIcon },
              { id: 'examples', label: 'Examples', icon: ServerIcon },
              { id: 'troubleshooting', label: 'Troubleshooting', icon: ExclamationTriangleIcon }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4 mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Hero Section */}
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                Agent Registration API
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-4xl mx-auto">
                Register WebRTC-capable agents with your organization's telephony backend. 
                This API enables AI agents to participate in voice conversations securely.
              </p>
              
              {/* Quick Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <ServerIcon className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  <h3 className="font-semibold text-gray-900 mb-1">Single Endpoint</h3>
                  <p className="text-sm text-gray-600">One API call to register agents</p>
                </div>
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <KeyIcon className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  <h3 className="font-semibold text-gray-900 mb-1">Secure Authentication</h3>
                  <p className="text-sm text-gray-600">Company-level bearer token security</p>
                </div>
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <CodeBracketIcon className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  <h3 className="font-semibold text-gray-900 mb-1">RESTful Design</h3>
                  <p className="text-sm text-gray-600">Standard HTTP methods and responses</p>
                </div>
              </div>
            </div>

            {/* What It Does */}
            <div className="bg-white rounded-lg border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">What This API Does</h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-900">Agent Authentication</h3>
                    <p className="text-sm text-gray-600">Validates agent identity and issues secure credentials</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-900">WebRTC Connection Setup</h3>
                    <p className="text-sm text-gray-600">Provides WebSocket URL and tokens for voice communication</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-900">Permission Management</h3>
                    <p className="text-sm text-gray-600">Restricts access to company-level agents only</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Try It Section */}
            <div className="bg-white rounded-lg border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Try It Out</h2>
              <p className="text-gray-600 mb-6">
                Use the interactive cURL generator below to test agent registration:
              </p>
              <CurlGenerator {...agentRegistrationTemplate} />
            </div>
          </div>
        )}

        {activeTab === 'api' && (
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">API Reference</h2>
              <p className="text-gray-600 mb-8">
                Complete technical documentation for the Agent Registration API.
              </p>
            </div>

            {/* Endpoint */}
            <div className="bg-white rounded-lg border border-gray-200 p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Endpoint</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-gray-700">Method:</span>
                  <span className="ml-2 font-mono text-sm bg-gray-100 px-2 py-1 rounded">POST</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">URL:</span>
                  <span className="ml-2 font-mono text-sm bg-gray-100 px-2 py-1 rounded">https://api.taar.ai/ext/agent/register</span>
                </div>
                <p className="text-sm text-gray-600 mt-4">
                  Use this endpoint to register a WebRTC-capable agent with your organization's telephony backend.
                </p>
              </div>
            </div>

            {/* Request Body */}
            <div className="bg-white rounded-lg border border-gray-200 p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Request Body</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Field</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Required</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">agentId</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">string</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">Unique identifier for the agent (typically email format)</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">nonce</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">string</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">One-time random value for request validation and security</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Response */}
            <div className="bg-white rounded-lg border border-gray-200 p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Response</h3>
              
              <h4 className="font-medium text-gray-900 mb-3">Success Response (200)</h4>
              <div className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto mb-4 relative">
                <pre className="text-sm">
                  <code>{apiExamples.response}</code>
                </pre>
                <button
                  onClick={() => copyToClipboard(apiExamples.response, 'response')}
                  className="absolute top-2 right-2 p-1 hover:bg-gray-700 rounded"
                >
                  <ClipboardDocumentIcon className="w-4 h-4" />
                </button>
              </div>

              <h4 className="font-medium text-gray-900 mb-3">Error Response (4xx/5xx)</h4>
              <div className="bg-gray-900 text-red-400 p-4 rounded-lg overflow-x-auto relative">
                <pre className="text-sm">
                  <code>{apiExamples.error}</code>
                </pre>
                <button
                  onClick={() => copyToClipboard(apiExamples.error, 'error')}
                  className="absolute top-2 right-2 p-1 hover:bg-gray-700 rounded"
                >
                  <ClipboardDocumentIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'auth' && (
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Authentication</h2>
              <p className="text-gray-600 mb-8">
                Secure your API requests with company-level bearer token authentication.
              </p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Bearer Token Authentication</h3>
              <p className="text-gray-600 mb-4">
                All requests must be authenticated using your Company Long-Lived Auth Token:
              </p>
              
              <div className="bg-gray-900 text-green-400 p-4 rounded-lg mb-6 relative">
                <code>Authorization: Bearer &lt;company-auth-token&gt;</code>
                <button
                  onClick={() => copyToClipboard('Authorization: Bearer <company-auth-token>)', 'auth-header')}
                  className="absolute top-2 right-2 p-1 hover:bg-gray-700 rounded"
                >
                  <ClipboardDocumentIcon className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <InformationCircleIcon className="w-5 h-5 text-blue-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">Getting Your Token</h4>
                    <p className="text-sm text-gray-600">Obtain your company bearer token from the Taar.AI dashboard</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <InformationCircleIcon className="w-5 h-5 text-blue-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">Security Scope</h4>
                    <p className="text-sm text-gray-600">Tokens are company-level and provide access to all your organization's agents</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <InformationCircleIcon className="w-5 h-5 text-blue-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">Token Validity</h4>
                    <p className="text-sm text-gray-600">Company tokens are long-lived and don't expire automatically</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <div className="flex">
                <ExclamationTriangleIcon className="w-5 h-5 text-yellow-400 mt-0.5 mr-3" />
                <div>
                  <h4 className="font-medium text-yellow-800">Important Security Note</h4>
                  <p className="text-sm text-yellow-700 mt-1">
                    Keep your bearer token secure and never expose it in client-side code. 
                    Use server-side API calls or secure environment variables.
                  </p>
                </div>
              </div>
        </div>
          </div>
        )}

        {activeTab === 'examples' && (
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Code Examples</h2>
              <p className="text-gray-600 mb-8">
                Ready-to-use code examples in different programming languages and tools.
              </p>
            </div>

            {/* cURL Example */}
            <div className="bg-white rounded-lg border border-gray-200 p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">cURL</h3>
              <div className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto relative">
                <pre className="text-sm">
                  <code>{apiExamples.curl}</code>
                </pre>
                <button
                  onClick={() => copyToClipboard(apiExamples.curl, 'curl')}
                  className="absolute top-2 right-2 p-1 hover:bg-gray-700 rounded"
                >
                  <ClipboardDocumentIcon className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* JavaScript Example */}
            <div className="bg-white rounded-lg border border-gray-200 p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">JavaScript (Node.js/Fetch)</h3>
              <div className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto relative">
                <pre className="text-sm">
                  <code>{apiExamples.js}</code>
                </pre>
                <button
                  onClick={() => copyToClipboard(apiExamples.js, 'js')}
                  className="absolute top-2 right-2 p-1 hover:bg-gray-700 rounded"
                >
                  <ClipboardDocumentIcon className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Next Steps */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex">
                <InformationCircleIcon className="w-5 h-5 text-blue-400 mt-0.5 mr-3" />
                <div>
                  <h4 className="font-medium text-blue-800">Next Steps</h4>
                  <ol className="text-sm text-blue-700 mt-2 space-y-1">
                    <li>1. Register your agent using this API</li>
                    <li>2. Use the returned credentials to connect via WebRTC</li>
                    <li>3. Follow the <a href="/docs/webrtc-client" className="underline">WebRTC Client guide</a> for integration</li>
                  </ol>
                </div>
          </div>
        </div>
          </div>
        )}

        {activeTab === 'troubleshooting' && (
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Troubleshooting</h2>
              <p className="text-gray-600 mb-8">
                Common issues and their solutions when working with the Agent Registration API.
              </p>
            </div>

            {/* Common Issues */}
            <div className="space-y-6">
              <div className="bg-white rounded-lg border border-red-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mr-2" />
                  401 Unauthorized
                </h3>
                <p className="text-gray-600 mb-3">Invalid or missing authentication token.</p>
                <div className="bg-gray-50 p-3 rounded text-sm">
                  <strong>Causes:</strong>
                  <ul className="mt-1 space-y-1 text-gray-600">
                    <li>• Missing Authorization header</li>
                    <li>• Incorrect token format (missing 'Bearer ' prefix)</li>
                    <li>• Invalid or expired company token</li>
                    <li>• Token doesn't belong to your organization</li>
                  </ul>
          </div>
        </div>

              <div className="bg-white rounded-lg border border-yellow-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500 mr-2" />
                  400 Bad Request
                </h3>
                <p className="text-gray-600 mb-3">Invalid request body or missing required fields.</p>
                <div className="bg-gray-50 p-3 rounded text-sm">
                  <strong>Causes:</strong>
                  <ul className="mt-1 space-y-1 text-gray-600">
                    <li>• Missing required 'agentId' field</li>
                    <li>• Missing required 'nonce' field</li>
                    <li>• Invalid JSON format in request body</li>
                    <li>• Agent ID in incorrect format</li>
        </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-blue-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <InformationCircleIcon className="w-5 h-5 text-blue-500 mr-2" />
                  409 Conflict
                </h3>
                <p className="text-gray-600 mb-3">Agent with the same ID already exists.</p>
                <div className="bg-gray-50 p-3 rounded text-sm">
                  <strong>Solution:</strong>
                  <ul className="mt-1 space-y-1 text-gray-600">  
                    <li>• Use a different agent ID</li>
                    <li>• Check if agent is already registered</li>
                    <li>• Consider reusing existing agent credentials</li>
          </ul>
        </div>
      </div>
            </div>

            {/* Best Practices */}
            <div className="bg-white rounded-lg border border-gray-200 p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Best Practices</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">Store Credentials Securely</h4>
                    <p className="text-sm text-gray-600">Save returned credentials server-side only, never in client code</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">Generate Valid Nonces</h4>
                    <p className="text-sm text-gray-600">Use cryptographically secure random values for the nonce field</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">Error Handling</h4>
                    <p className="text-sm text-gray-600">Always check response status and handle errors gracefully</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Copy Success Indicator */}
        {copiedCode && (
          <div className="fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg">
            Code copied to clipboard!
          </div>
        )}
    </main>
  </div>
);
};

export default AgentRegistrationAPI;
